/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { datadogRum } from "@datadog/browser-rum";
import styled from "styled-components";

import * as API from "../../../../api/billing";
import Button from "../../../reusable/button";
import Card from "../../../reusable/cardv2";
import CollapsibleTable from "../../../reusable/reactTable";
import ErrorMessage from "../../../reusable/errorMessage";
import InvoiceDownloadIcon from "../../../reusable/invoiceDownloadIcon";
import MakePaymentButton from "./makePaymentButton";
import ManualInvoiceModalBody from "./manualInvoiceModalBody";
import Modal from "../../../reusable/reactModal";
import Tooltip from "../../../reusable/tooltip";
import {
  BillingType,
  RecentInvoicesCardPropTypes,
  RecentInvoicesData,
} from "../lib/types";
import {
  StyledCircle,
  StyledCircleGray,
  StyledCircleGreen,
  StyledCircleRed,
  StyledCircleYellow,
  StyledErrorContainer,
} from "../lib/styles";
import {
  defaultRecentInvoicesData,
  genericErrorMessage,
  recentInvoicesTableHeaders,
} from "../lib/constants";
import { redesignTheme } from "../../../theme/theme";

const StyledText = styled.p`
  padding: 0px 24px 32px;
  margin-bottom: 0;
  color: ${redesignTheme.color.gray900};
  font-size: ${redesignTheme.fontSize[100]};
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${redesignTheme.spacing[1.5]};
`;

const StyledStatus = styled.p`
  text-transform: uppercase;
  font-size: ${redesignTheme.fontSize[1]};
  font-weight: ${redesignTheme.fontWeight.extrabold};
  letter-spacing: 1px;
  line-height: 20px;
  display: inline-block;
  margin-bottom: 0;
`;

const StyledDate = styled.p`
  font-size: ${redesignTheme.fontSize[1]};
  color: ${redesignTheme.color.gray900};
  margin-bottom: 0;
`;

const StyledPaymentMessage = styled.div`
  margin-top: ${redesignTheme.spacing[2.5]};
  color: ${redesignTheme.color.red50};
`;

const StyledFailedPayment = styled.span`
  padding-left: 5px;
  font-size: ${redesignTheme.fontSize[1]};
`;

const StyledButton = styled(Button)`
  margin: auto;
  float: right;
  position: relative;
  right: 24px;
  top: -45px;
`;

const failedPaymentMessage = (message: string): React.ReactElement => {
  return (
    <StyledPaymentMessage>
      <i className="fas fa-exclamation-triangle" />
      <Tooltip content={message} placement="top">
        <StyledFailedPayment data-testid="failed-payment-message">
          {i18n.translate("admin_pages.activity.failed_payment")}
        </StyledFailedPayment>
      </Tooltip>
    </StyledPaymentMessage>
  );
};

const RecentInvoicesCard = ({
  showSuperAdminFeature,
  updateToastMessage,
}: RecentInvoicesCardPropTypes): React.ReactElement => {
  const [data, setData] = React.useState<RecentInvoicesData>(
    defaultRecentInvoicesData
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [modal, setModal] = React.useState<string>("");
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(5);

  const closeModal = (): void => {
    return setModal("");
  };

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await API.getRecentInvoices({
        page_number: pageNumber,
        page_size: rowsPerPage,
      });

      setData({
        lineItems: response.data.data.invoices,
        ...response.data.data.billing,
        ...response.data.meta,
      });
    } catch (error) {
      datadogRum.addError(error);
      setData({
        ...defaultRecentInvoicesData,
        errorMessage: genericErrorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [pageNumber, rowsPerPage]);

  const handlePaginationChange = (value): void => {
    setPageNumber(value);
  };

  const handleRowsPerPageChange = (event): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const getColoredCircle = (status: string): React.ReactElement => {
    if (status === "paid") {
      return <StyledCircleGreen data-testid="green-circle" />;
    }
    if (status === "posted" || status === "pending") {
      return <StyledCircleYellow data-testid="yellow-circle" />;
    }
    if (status === "voided") {
      return <StyledCircleGray data-testid="gray-circle" />;
    }
    if (status === "not_paid" || status === "payment_due") {
      return <StyledCircleRed data-testid="red-circle" />;
    }
    return <StyledCircle data-testid="empty-circle" />;
  };

  const unpaidStatuses = ["not_paid", "payment_due", "pending", "posted"];
  const paymentStatuses = ["not_paid", "payment_due", "posted"];

  const getStatusDate = (item): string => {
    if (item.status === "paid") {
      return item.dateInvoicePaid;
    }
    if (item.status === "voided") {
      return item.dateInvoiceVoided;
    }
    return item.dateInvoiceDue;
  };

  const getInvoiceType = (description) => {
    switch (true) {
      case description.includes("Subscription"):
      case description.includes("Proration"):
        return "subscription";
      case description.includes("Reward"):
        return "reward";
      default:
        return "";
    }
  };

  const rowData = data.lineItems.map((item, index) => {
    const invoiceType = getInvoiceType(item.displayDescription);
    const billingType = data.billingType;
    const rewardsArePaidWithCC =
      billingType === BillingType.Flex ||
      billingType === BillingType.PayAsYouGo;
    const subscriptoinIsPaidWithCC = data.subscriptionAutoCollection === "on";
    const invoiceIsPaidWithCC =
      (invoiceType === "reward" && rewardsArePaidWithCC) ||
      (invoiceType === "subscription" && subscriptoinIsPaidWithCC);

    if (item.id) {
      item.downloadIcon = (
        <InvoiceDownloadIcon
          invoiceId={item.id}
          testId={`download-button-${index}`}
          updateToastMessage={updateToastMessage}
        />
      );
    }

    item.statusBox = (
      <div>
        <StatusWrapper>
          {getColoredCircle(item.status)}
          <StyledStatus>{item.displayStatus}</StyledStatus>
        </StatusWrapper>
        <StyledDate>
          {unpaidStatuses.includes(item.status) &&
            `${i18n.translate("admin_pages.activity.due")} `}
          {getStatusDate(item)}
        </StyledDate>
        {item.status !== "paid" &&
          item.displayError &&
          failedPaymentMessage(item.displayError)}
      </div>
    );

    item.makePaymentButton = (
      <div>
        {invoiceIsPaidWithCC && paymentStatuses.includes(item.status) && (
          <MakePaymentButton
            prevPaymentError={item.displayError}
            testId={`make-payment-button-${index}`}
            updateToastMessage={updateToastMessage}
          />
        )}
      </div>
    );

    return item;
  });

  return (
    <Card
      bodyPadding="16px 0px 0px"
      testId="recent-invoices"
      title={i18n.translate("admin_pages.activity.recent_invoices")}
    >
      {data.errorMessage ? (
        <StyledErrorContainer>
          <ErrorMessage
            errorMessage={data.errorMessage}
            testId="recent-invoices-error"
          />
        </StyledErrorContainer>
      ) : (
        <>
          {showSuperAdminFeature &&
            (data.billingType === BillingType.Flex ||
              data.billingType === BillingType.Fixed) && (
              <StyledButton
                onClick={(): void => {
                  return setModal("manualInvoice");
                }}
                size="small"
                testId="manual-invoice-button"
                variant="superAdmin"
              >
                {i18n.translate("admin_pages.activity.manual_invoice.button")}
              </StyledButton>
            )}
          <StyledText>
            {i18n.translate("admin_pages.activity.recent_invoices_description")}
          </StyledText>
          <CollapsibleTable
            handlePaginationChange={handlePaginationChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
            headers={recentInvoicesTableHeaders}
            loading={loading}
            pageNumber={pageNumber}
            rowCount={data.rowCount}
            rows={rowData}
            rowsPerPage={rowsPerPage}
          />
        </>
      )}
      <Modal
        ariaLabel="manual invoice modal"
        close={closeModal}
        showDialog={modal === "manualInvoice"}
        testId="manual-invoice-modal"
        title={i18n.translate(
          "admin_pages.activity.manual_invoice.modal_title"
        )}
      >
        <ManualInvoiceModalBody
          closeModal={closeModal}
          companyId={data.companyId}
          fetchData={fetchData}
          netTermDays={data.netTermDays}
          prepayAmount={data.refillAmount}
          prepayAmountDisplay={data.refillAmountDisplay}
          prepayThresholdDisplay={data.refillThresholdDisplay}
          rewardBalanceDisplay={data.rewardBalanceDisplay}
          updateToastMessage={updateToastMessage}
        />
      </Modal>
    </Card>
  );
};

export default RecentInvoicesCard;
